import { DATE_PRESETS, YEAR_DISPLAYMENT_MAP } from "@/consts";
import type { DatePresets } from "@/types";

export function useDates() {
  const format = (
    preset: DatePresets,
    date: Date,
    locale?: Intl.LocalesArgument,
    additionalOptions?: Intl.DateTimeFormatOptions,
  ) => {
    // Getting the correct options
    const options = DATE_PRESETS[preset];

    // Setting the navigator's language as the locale if it's not specified
    locale = locale || navigator.language || "da";

    // We should show 12-hours format only on en-GB
    options.hour12 = locale === "en-GB";

    // If it's the same year, and the preset is supported year displayment, we
    // should activate the year displayment
    const sameYear = date.getFullYear() === new Date().getFullYear();
    if (YEAR_DISPLAYMENT_MAP.includes(preset) && !sameYear) {
      options.year = "numeric";
    } else {
      delete options.year;
    }

    // Let's format the date
    const formatted = new Intl.DateTimeFormat(locale, {
      ...options,
      ...additionalOptions,
    }).format(date);

    if (preset === "time" && (locale === "da" || locale === "no")) {
      return `kl. ${formatted}`;
    }

    return formatted;
  };

  /**
   * Ensures that "kl." is correctly inserted before time values in Danish or
   * Norwegian date ranges formatted using Intl.DateTimeFormat.formatRange.
   *
   * - If the range is on the same day: inserts "kl." only before the first time.
   * - If the range spans multiple days: inserts "kl." before each time (if not already present).
   */
  const ensureKlInScandinavianRange = (str: string, locale: string) => {
    const isNorwegian = locale === "no";
    const timePattern = isNorwegian ? "\\d{2}:\\d{2}" : "\\d{2}\\.\\d{2}";
    const timeRegex = new RegExp(`(?<!kl\\. )(${timePattern})`, "g");
    const matches = [...str.matchAll(timeRegex)];

    if (matches.length < 1) return str;

    const [, afterDash] = str.split(" - ");
    const hasSecondDate =
      /\d{1,2}\.\s?[a-zæøå]+/i.test(afterDash || "") || // e.g. "10. mars"
      /\d{4}/.test(afterDash || ""); // includes a year

    if (!hasSecondDate && matches.length >= 1) {
      // Same day: insert "kl." before first time only
      return str.replace(timeRegex, (match, p1, offset) => {
        if (offset === matches[0]?.index) return `kl. ${p1}`;
        return p1;
      });
    }

    // Different dates: insert "kl." before all times not already prefixed
    return str.replace(timeRegex, "kl. $1");
  };

  const formatRange = (
    start: Date,
    end: Date,
    locale?: Intl.LocalesArgument,
  ) => {
    // Setting the navigator's language as the locale if it's not specified
    locale = locale || navigator.language || "da";

    // The default options
    const options: Intl.DateTimeFormatOptions = {};

    // Is this the same year?
    const sameYear = start.getFullYear() === end.getFullYear();

    // Day and month should always be displayed
    options.day = "numeric";
    options.month = "long";

    // We should use the hour12 format only in en-GB
    options.hour12 = locale === "en-GB";

    const formatter = new Intl.DateTimeFormat(locale, {
      ...options,
      ...{ hour: "2-digit", minute: "2-digit" },
      ...(sameYear ? {} : { year: "numeric" }),
    });

    // Apply the same formats for consistency
    const [first, second] = formatter
      .formatRange(start, end)
      // Replace different dash (-) formats
      .replace(/[\u2012\u2013\u2014]/g, "-")
      // Replace different spaces formats
      .replace(/[\u00A0\u202F\u2008\u2002]/g, " ")
      .split("-");

    const formattedString = `${first?.trim()} - ${(second || "").trim()}`;

    return ensureKlInScandinavianRange(formattedString, locale as string);
  };

  const formatByOptions = (
    date: Date,
    options: Intl.DateTimeFormatOptions,
    locale?: Intl.LocalesArgument,
  ) => {
    // Setting the navigator's language as the locale if it's not specified
    locale = locale || navigator.language || "da";

    // Let's format the date
    return new Intl.DateTimeFormat(locale, options).format(date);
  };

  const getWeekdayNames = (locale?: Intl.LocalesArgument) => {
    // Setting the navigator's language as the locale if it's not specified
    locale = locale || navigator.language || "da";

    const options: Intl.DateTimeFormatOptions = { weekday: "short" };

    // Monday, August 2, 2021 is a known Monday
    const baseDate = new Date(Date.UTC(2021, 7, 2));
    const formatter = new Intl.DateTimeFormat(locale, options);

    const result: Record<number, string> = {};
    for (let i = 0; i < 7; i++) {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + i);
      result[i] = formatter.format(date);
    }

    return result;
  };

  return {
    format,
    formatRange,
    formatByOptions,
    ensureKlInScandinavianRange,
    getWeekdayNames,
  };
}
